import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';
import { PermissionService } from '@app/shared/services';
import { BaseService } from '@app/shared/services/base/base.service';
import { EntityServiceProvider } from '@app/shared/services/datasource/entity-service-provider.service';
import { StateService } from '@app/shared/services/state-service/state.service';
import {
  ActionButton,
  COMMON_ACTION_TYPES,
  IBaseEntityType,
  SupportedAppCodes,
} from '@app/shared/types';
import { APIActions } from '@app/store/actions/api-actions/api.actions';
import { AgRendererComponent } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import {
  get,
  includes,
  isArray,
  isEmpty,
  isFunction,
  isNil,
  map,
  find,
} from 'lodash';
import {
  ActionEmitter,
  IActionEmitterParams,
} from './common-actions-renderer.types';

@Component({
  selector: 'xvi-common-actions-renderer',
  templateUrl: './common-actions-renderer.component.html',
  styleUrls: ['./common-actions-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class CommonActionsRendererComponent implements AgRendererComponent {
  public params: ICellRendererParams;
  public entityType: string;
  public entityService: BaseService<IBaseEntityType>;
  public statusPath: string = 'status';
  public data: IBaseEntityType;
  public actionButtons: ActionButton[];
  public permissions: string[] = [];
  public entityNameToCheckPermissions: string;

  // eslint-disable-next-line max-params
  public constructor(
    private readonly router: Router,
    private readonly i18nService: XVII18nService,
    private readonly entityServiceProvider: EntityServiceProvider,
    private readonly stateService: StateService,
    private readonly apiActions: APIActions,
  ) {}

  public agInit(params: ICellRendererParams): void {
    this.params = params;
    this.data = params.node.data;
    this.statusPath = get(this.params, 'statusPath', this.statusPath);
    this.entityType = get(this.params, 'entityType', null);
    this.entityService = isEmpty(this.entityType)
      ? null
      : this.entityServiceProvider.getService(this.entityType);
    this.entityNameToCheckPermissions = get(
      this.entityService,
      'config.entityNameToCheckPermissions',
      this.entityType,
    );

    this.permissions = get(this.params, 'permissions', [
      PermissionService.composeViewPermissionName(
        this.entityNameToCheckPermissions,
        {
          appCode: SupportedAppCodes.XVI,
          camelCase: true,
        },
      ),
    ]);

    this.initActionButtons();
  }

  public refresh(): boolean {
    const defaultRequestParams = {
      query: {},
      options: {},
    };

    const previousRequest = this.stateService.getByPath(
      ['entities', this.entityType, 'previousRequest'],
      defaultRequestParams,
    );

    this.apiActions.fetchList(
      this.entityType,
      previousRequest.query,
      previousRequest.options,
    );

    return true;
  }

  public async changeStatusById(status: string): Promise<void> {
    await this.apiActions.updateEntityAsPromised(
      this.entityType,
      { status },
      { endpoint: this.data._id },
    );
    this.refresh();
  }

  public edit(): void {
    this.router.navigate(
      [this.entityService.config.uiRoute, get(this, 'data._id', '')],
      {},
    );
  }

  public emitAction(type: string): void {
    switch (type) {
      case COMMON_ACTION_TYPES.View:
      case COMMON_ACTION_TYPES.Edit:
        this.edit();

        // no need to emit event for view/edit action
        return;
      default:
        break;
    }
    const targetButton = find(get(this.params, 'actionButtons'), {
      type,
    });

    const emitParams: IActionEmitterParams = {
      type,
      data: this.data,
      rendererReference: this,
    };

    if (isFunction(targetButton?.click)) {
      targetButton.click(emitParams);
    }
    const paramsEmitter: ActionEmitter = get(this.params, 'paramsEmitter');

    if (!isFunction(paramsEmitter)) {
      return;
    }

    paramsEmitter(emitParams);
  }

  protected initActionButtons(): void {
    const paramButtons = get(
      this.params,
      'actionButtons',
      [],
    ) as ActionButton[];

    this.actionButtons = map(paramButtons, (btn) => {
      const btnVisible = btn.visible;

      if (!isNil(btnVisible) && isFunction(btnVisible)) {
        return {
          ...btn,
          visible: isFunction(btn.visible)
            ? btn.visible(this.data)
            : btn.visible,
        };
      }

      if (isArray(btnVisible)) {
        return {
          ...btn,
          visible: includes(btnVisible, this.data?.[this.statusPath]),
        };
      }

      // default is visible
      return {
        ...btn,
        visible: true,
      };
    });
  }
}
